import { Injectable } from '@angular/core';
import { ConfigService } from './config.service';
import { HttpClient } from '@angular/common/http';
import { ApiServiceService } from '../api-service.service';
import { LenderPassbookRecord, LenderPassbookReference } from '../model/lender-passbook-record';
import { Gender } from '../model/gender.model';
import { UserAddress } from '../model/user-address.model';
import { BankAccount, BankAccountType } from '../model/bank.model';
import { Lender } from '../model/lender.model';
import { LenderCommissionRateUtil } from '../utils/lender/lender-commission-rate.util';

export interface SplitMoney {
    principal: number;
    interest: number;
}

export interface InvestmentMetrics {
    principalOutstanding: number;
    numberOfPortfolios: number;
    totalInvestment: number;
    earnings: {
        penalInterest: number;
        emi: SplitMoney, prepayment: SplitMoney, settlement: SplitMoney, restructuring: SplitMoney
    };
    numberOfLoansInvestedIn: number;
    roi: number;
    numberOfLoansWithOngoingDelay: number;
    portfolioMetrics: [{ id: string, roi: number }];
    writtenOff: SplitMoney;
    numberOfWrittenOffLoans: number;
    // TODO capitalizedInterest to be changed to capitalizedInterestDueToMoratorium.
    capitalisedInterest: number;
    capitalizedInterestDueToRestructuring: number;
    numberOfLoansWithMoratorium: number;
    numberOfLoansAsNPA: number;
    numberOfSubStandardAssetLoans: number;
    numberOfLossAssetLoans: number;
    numberOfSettledLoans: number;
    numberOfRestructuredLoans: number;
}

@Injectable()
export class LenderDetailsService {
    private apiService: ApiServiceService;
    private http: HttpClient;
    private lenderCommissionRateUtil: LenderCommissionRateUtil;
    private lenderInfo: any;
    private metricsInfo: any;
    private previousLenderId: string = '';

    constructor(private configService: ConfigService, apiService: ApiServiceService, http: HttpClient, lenderCommissionRateUtil: LenderCommissionRateUtil) {
        this.apiService = apiService;
        this.http = http;
        this.lenderCommissionRateUtil = lenderCommissionRateUtil;
    }

    async createLenderDetails(profileId: string): Promise<any> {
        const url = this.configService.baseUrl() + 'lenderDetails';
        return await this.http.post(url, { profile_id: profileId }).toPromise();
    }

    async addPersonalInformation(lenderId: string, profileId: string, gender: Gender,
        dob: string, address: UserAddress, lenderApplicationCompletePercentage: number = 0): Promise<void> {
        const url = this.configService.baseUrl() + 'addLenderPersonalInfo';
        const response: any = await this.http.post(url, {
            lender_id: lenderId,
            profile_id: profileId,
            gender: gender,
            dob: dob,
            address: address.address,
            city: address.city,
            state: address.state,
            pincode: address.pin_code,
            lender_application_complete: lenderApplicationCompletePercentage
        }).toPromise();

        if (response.status !== 'success') {
            throw new Error(`Encountered an error while adding personal information. ${response}`);
        }
    }

    async addBankAccount(lenderId: string, bankAccount: BankAccount, userId: string): Promise<void> {
        const accountType = bankAccount.accountType === BankAccountType.Savings ? 'saving' : 'current';

        const body = {
            lender_id: lenderId,
            account_no: bankAccount.accountNumber,
            account_type: accountType,
            confirm_account_no: bankAccount.accountNumber,
            bank_name: bankAccount.name,
            bank_branch_name: bankAccount.branch,
            bank_city: bankAccount.city,
            bank_ifsc: bankAccount.ifsc,
            account_holder_name: bankAccount.accountHolderName,
            userId: userId
        };
        const response: any = await this.http.post(this.configService.baseUrl() + 'addBankDetailsLender', body).toPromise();

        if (response.status !== 'success') {
            throw new Error(`Encountered an error while adding bank account for lender: ${response}`);
        }
    }

    async getLenderDetails(lenderId: string): Promise<any> {
        const url = this.configService.baseUrl() + 'getLenderInfoDetails';
        this.lenderInfo = await this.http.post(url, { lender_id: lenderId }).toPromise();
        return this.lenderInfo;
    }

    async getLenderWalletAmountAndPhotoUrl(userId: string): Promise<any> {
        const url = `${this.configService.getUserServiceUrl()}/user/${userId}/lender-details`;
        const lenderDetails = this.http.get(url).toPromise();
        return lenderDetails;
    }

    public async getLenderApplicationStatusById(lenderId: string): Promise<any> {
        const url = this.configService.baseUrl() + 'getLenderApplicationStatusById';
        return await this.http.post(url, { lender_id: lenderId }).toPromise();
    }

    public async getLenderInfoDetails(lenderId: string): Promise<any> {
        if (this.lenderInfo === undefined) {
            await this.getLenderDetails(lenderId);
        }
        return this.lenderInfo;
    }

    async updateResidenceProof(proofType: string, proofNumber: string, lenderId: string, lenderApplicationComplete: number): Promise<void> {
        const url = this.apiService.baseURL() + 'updateLenderResidenceProof';
        const response: any = await this.http.post<{ status_code: number, status: string }>(url, {
            proof_type: proofType,
            proof_no: proofNumber,
            lender_id: lenderId,
            lender_application_complete: lenderApplicationComplete
        }).toPromise();
        if (response.status !== 'success' || response.status_code !== 201) {
            throw new Error(`Encountered an error while updating residence proof. ${response}`);
        }
    }

    public async getMetrics(lenderId: string): Promise<InvestmentMetrics> {
        const url = `${this.configService.baseUrl()}lender/${lenderId}/metrics`;
        this.metricsInfo = await this.http.get<InvestmentMetrics>(url).toPromise();
        return this.metricsInfo;
    }

    public async getMetricsInfo(lenderId: string): Promise<any> {
        if (this.previousLenderId != lenderId) {
            this.previousLenderId = lenderId;
            this.metricsInfo = await this.getMetrics(lenderId);
            return this.metricsInfo;
        } else {
            if (this.metricsInfo === undefined) {
                await this.getMetrics(lenderId);
            }
            return this.metricsInfo;
        }
    }

    public async getMetricsForPortfolioType(lenderId: string, portfolioType: string): Promise<InvestmentMetrics> {
        const url = `${this.configService.baseUrl()}lender/${lenderId}/metrics?portfolioType=${portfolioType}`;
        return this.http.get<InvestmentMetrics>(url).toPromise();
    }

    public async getPassbook(lenderId: string, fromDate: string, toDate: string): Promise<LenderPassbookRecord[]> {
        const lenderPassbookRecord: LenderPassbookRecord[] = [];
        const url = `${this.configService.baseUrl()}passbook?lenderId=${lenderId}&from=${fromDate}&to=${toDate}`;
        const response: any = await this.http.get<{ _id: string, loan_id: string, amount: number, transaction_type: string, reference: string, timestamp: string }[]>(url).toPromise();

        response.forEach(item => {
            lenderPassbookRecord.push(new LenderPassbookRecord(item.amount, new Date(item.timestamp),
                this.buildReferenceText(item.reference.toString()), item.loan_id, this.lenderCommissionRateUtil));
        });
        return lenderPassbookRecord;
    }

    public async getLenderPassbookRecordsCount(lenderId: string): Promise<any> {
        const url = `${this.configService.baseUrl()}getLenderPassbookRecordsCount`;
        return await this.http.post(url, { lender_id: lenderId }).toPromise();
    }

    public async associateDocuments(lenderId: string, documentIds: string[]): Promise<void> {
        const url = `${this.configService.baseUrl()}lender/${lenderId}/documents`;
        await this.http.post(url, { documentIds: documentIds }, { responseType: 'text' }).toPromise();
    }

    public async getLenderReturns(lenderId: string): Promise<{ expectedEmiDate: Date, expectedEmiAmount: number }[]> {
        const url = `${this.configService.baseUrl()}lender/${lenderId}/returns`;
        return this.http.get<{ expectedEmiDate: Date, expectedEmiAmount: number }[]>(url).toPromise();
    }

    public async getLenderDetailsByProfileId(profileId: string): Promise<Lender> {
        const url = `${this.configService.baseUrl()}lender?userId=${profileId}`;
        try {
            return await this.http.get<Lender>(url).toPromise();
        } catch (e) {
            return null;
        }
    }

    public async getLenderInfo(userId: string): Promise<any> {
        const url = `${this.configService.baseUrl()}lender?userId=${userId}`;
        return await this.http.get(url).toPromise();
    }


    private buildReferenceText(reference: string): string {
        if (reference.includes(LenderPassbookReference.WriteOff)) {
            return LenderPassbookReference.WriteOff;
        } else if (reference.includes(LenderPassbookReference.WriteBack)) {
            return LenderPassbookReference.WriteBack;
        } else {
            return reference;
        }
    }
}
